import type { BlockModel, Store } from '@blocksuite/store';

const COLUMN = 'affine:column';
const COLUMNS = 'affine:columns';

/**
 * Containers that hold content in their own editable compartment.
 *
 * Listed explicitly rather than derived from `role: 'hub'`: `affine:note` and
 * `affine:callout` are hubs too, and treating them as compartments would change
 * long-standing backspace behaviour at the top of a callout.
 */
const CONTAINER_FLAVOURS = [COLUMN, 'affine:rich-table-cell'];

/**
 * Column-layout tree helpers.
 *
 * These live in `shared` rather than in the columns block package because text
 * editing has to know about column boundaries: `affine:paragraph`'s backspace
 * and delete handlers run before any column-scoped keymap could, so they need
 * the rules here directly. Everything is matched by flavour string, so nothing
 * in `shared` has to depend on the columns package.
 */

/** Nearest ancestor of `block` whose flavour is in `flavours`, `block` included. */
function closestFlavour(
  block: BlockModel | null | undefined,
  flavours: string[]
): BlockModel | null {
  let current: BlockModel | null = block ?? null;
  while (current) {
    if (flavours.includes(current.flavour)) return current;
    current = current.parent;
  }
  return null;
}

/**
 * The column or table cell containing `block`, or null when it is not inside
 * either.
 */
export function getContainerAncestor(
  block: BlockModel | null | undefined
): BlockModel | null {
  return closestFlavour(block, CONTAINER_FLAVOURS);
}

/** The `affine:columns` row containing `block`, or null. */
export function findColumnsRow(
  block: BlockModel | null | undefined
): BlockModel | null {
  return closestFlavour(block, [COLUMNS]);
}

/**
 * True when `a` and `b` sit in different containers, or when one is inside a
 * container and the other is not.
 *
 * Text edits that join two blocks must stop at this boundary: in document
 * order the last block of one column is followed by the first block of the
 * next, but visually they are side by side, so merging them would silently
 * move content between columns.
 */
export function crossesContainerBoundary(
  a: BlockModel | null | undefined,
  b: BlockModel | null | undefined
): boolean {
  const containerA = getContainerAncestor(a);
  const containerB = getContainerAncestor(b);
  if (!containerA && !containerB) return false;
  return containerA?.id !== containerB?.id;
}

/**
 * Restores the invariants a columns row must hold after any mutation: empty
 * columns are dropped, and a row down to one column (or none) is unwrapped
 * back into plain siblings of its parent.
 *
 * Every path that can empty a column — a drag out, a backspace, a delete —
 * has to run this, or the doc accumulates invisible empty columns that still
 * take up horizontal space.
 *
 * @returns true if the row was unwrapped or removed.
 */
export function normalizeColumns(
  store: Store,
  row: BlockModel | null | undefined
): boolean {
  if (!row || row.flavour !== COLUMNS || store.readonly) return false;
  // A row deleted by an earlier normalize pass is no longer reachable.
  if (!store.getBlock(row.id)) return false;

  const parent = row.parent;
  if (!parent) return false;

  row.children
    .filter(child => child.flavour === COLUMN && child.children.length === 0)
    .forEach(column => store.deleteBlock(column));

  const remaining = row.children.filter(child => child.flavour === COLUMN);
  if (remaining.length > 1) return false;

  const survivor = remaining[0];
  if (survivor && survivor.children.length > 0) {
    // Hoist the last column's content into the row's place, so unwrapping
    // never loses blocks.
    store.moveBlocks(survivor.children.slice(), parent, row, true);
  }
  store.deleteBlock(row);
  return true;
}

/**
 * Handles a caret-at-start deletion that would otherwise merge across a
 * container boundary: if `block` is the only thing left in its column, the
 * column goes away (and the row with it, when nothing else remains).
 *
 * Table cells are deliberately exempt — a cell is a fixed slot in the grid, so
 * emptying it must not delete it.
 *
 * @returns true if a container was removed.
 */
export function dropContainerIfEmptied(
  store: Store,
  block: BlockModel | null | undefined
): boolean {
  if (!block || store.readonly) return false;

  const container = getContainerAncestor(block);
  // Only the container's own single child can collapse it; a deeper or later
  // block just loses its own merge.
  if (!container || container.children.length !== 1) return false;
  if (container.children[0]?.id !== block.id) return false;
  if (block.children.length > 0) return false;
  if (block.text?.length) return false;

  // A table cell is a fixed slot in a grid — it must stay, just empty.
  if (container.flavour !== COLUMN) return false;

  const row = container.parent;
  store.deleteBlock(container);
  normalizeColumns(store, row);
  return true;
}

/**
 * Tidies up the container a block was just dragged out of.
 *
 * The two container kinds want opposite things: an emptied column should
 * disappear (and unwrap its row when it was the last one), while an emptied
 * table cell must stay — it is a fixed slot in the grid — but needs an empty
 * paragraph put back so it is still clickable.
 */
export function normalizeContainerAfterMove(
  store: Store,
  container: BlockModel | null | undefined
): void {
  if (!container || store.readonly) return;
  // A container deleted by an earlier pass is no longer reachable.
  if (!store.getBlock(container.id)) return;

  if (container.flavour === COLUMN) {
    normalizeColumns(store, findColumnsRow(container));
    return;
  }

  if (
    container.flavour === 'affine:rich-table-cell' &&
    container.children.length === 0
  ) {
    store.addBlock('affine:paragraph', {}, container.id);
  }
}
